import apiRoutes from 'config/apiRoute';
import { all, call, put, select, StrictEffect, takeLatest } from 'redux-saga/effects';
import api from 'utils/api';
import { AxiosResponse } from 'utils/interface';
import { RootState } from 'store';

import {
  fetchTeamTaskActivity,
  setTeamTaskActivityLoading,
  setTeamTaskActivityRevalidating,
  setTeamTaskActivitySuccess,
  setTeamTaskActivityError,
  TeamTaskUser,
  TeamGroup,
} from './teamTaskActivity-slice';

const { get } = api;

const buildTeamTaskUser = (user: any): TeamTaskUser => ({
  uuid: user.uuid,
  name: user.name,
  email: user.email,
  avatar: user.avatar || null,
  last_activity: user.last_activity,
  activity_status: user.activity_status,
  resolvedStatus: user.activity_status ?? 'offline',
});

/**
 * worker saga: loads the internal team and every task currently sitting in the
 * `in_progress` status. Grouping by user is done in the view so the store stays
 * a plain cache of both lists.
 */
export function* fetchTeamTaskActivitySagaListener(): any {
  const isLoading = yield select((store: RootState) => store.teamTaskActivity.loading);
  if (isLoading) {
    return;
  }

  try {
    const hasUsers = yield select((store: RootState) => store.teamTaskActivity.users.length > 0);

    if (hasUsers) {
      yield put(setTeamTaskActivityRevalidating());
    } else {
      yield put(setTeamTaskActivityLoading());
    }

    const [contactsRes, tasksRes, groupsRes]: AxiosResponse[] = yield all([
      call(get, apiRoutes.CONTACTS, {
        'filters[type][]': 'regular_user',
        'filters[billing_contacts.status][]': 'active',
        'page[size]': 999,
      }),
      call(get, apiRoutes.PROJECT_TASKS, {
        'filters[task_status][]': 'in_progress',
        'page[size]': 1000,
        'page[number]': 1,
        'sort[by]': 'task_sort',
        'sort[direction]': 'asc',
      }),
      call(get, apiRoutes.GROUPS, { 'page[size]': 100 }),
    ]);

    const contacts = contactsRes.data.data || [];
    const users: TeamTaskUser[] = contacts
      .map((contact: any) => contact.user)
      .filter((user: any) => user && user.uuid)
      .map(buildTeamTaskUser);

    const tasks = tasksRes.data.data || [];

    const groups: TeamGroup[] = (groupsRes.data.data || [])
      .filter((group: any) => group?.uuid && group?.name && Array.isArray(group.users))
      .map((group: any) => ({
        uuid: group.uuid,
        name: group.name,
        users: group.users
          .filter((groupUser: any) => groupUser?.uuid)
          .map((groupUser: any) => ({ uuid: groupUser.uuid, name: groupUser.name })),
      }))
      .filter((group: TeamGroup) => group.users.length > 0);

    yield put(setTeamTaskActivitySuccess({ users, tasks, groups }));
  } catch (e) {
    yield put(setTeamTaskActivityError());
  }
}

/**
 * watcher saga
 */
function* teamTaskActivitySaga(): Generator<StrictEffect> {
  yield takeLatest(fetchTeamTaskActivity.type, fetchTeamTaskActivitySagaListener);
}

export default teamTaskActivitySaga;
